<template>
    <form-modal
        ref="formModal"
        :title="i18nTitleModal"
        :in-editing="isEdit"
        size="md"
        :handle-close="handleCancel">
        <template v-slot:form>
            <form @submit.prevent="handleOk">
                <feedback-modal
                    ref="feedbackModal"
                    :title="i18nLimitJustification"
                />
                <b-row class="mb-2">
                    <b-col class="col-sm-12">
                        <b-form-group
                            class="label-pdv"
                            :label="getI18n('JUSTIFICATION.FLAGS', 'enable_limit')"
                            label-for="enable_limit"
                        >
                            <b-form-checkbox
                                id="enable_limit"
                                v-model="enableLimit"
                                size="lg"
                                switch
                            />
                        </b-form-group>
                    </b-col>
                </b-row>
                <b-row v-if="enableLimit" class="mb-2">
                    <b-col class="col-sm-12">
                        <b-form-group
                            class="required label-pdv"
                            :label="i18nMaxConsecutiveUses"
                            label-for="max-consecutive-uses-input"
                            :invalid-feedback="i18nInvalidMaxConsecutiveUsesLabel">
                            <b-form-input
                                id="max-consecutive-uses-input"
                                v-model.number="$v.limitJustificationModal.max_consecutive_uses.$model"
                                class="input-pdv-blue"
                                type="number"
                                min="1"
                                autocomplete="off"
                                :disabled="submitLoading"
                                :state="validateField('max_consecutive_uses', 'limitJustificationModal')"
                                @input="clearResponseError('max_consecutive_uses', 'limitJustificationModal')"
                                @blur="$v.limitJustificationModal.max_consecutive_uses.$touch"
                            ></b-form-input>
                        </b-form-group>
                    </b-col>
                </b-row>
            </form>
        </template>
        <template v-slot:footer>
            <div class="d-flex justify-content-center">
                <cancel-button
                    ref="cancelButton"
                    child-class="col-sm-6 float-left"
                    :disabled="submitLoading"
                    @onClick="handleCancel"
                />
                <save-button
                    ref="okButton"
                    child-class="col-sm-6 float-right ml-3"
                    :busy="submitLoading"
                    @onClick="handleSubmit"
                />
            </div>
        </template>
    </form-modal>
</template>

<script>
import { integer, minValue, required } from 'vuelidate/lib/validators'
import TenantService from '@src/services/TenantService'
import FormModal from '@/src/components/FormModal.vue'
import FeedbackModal from '@src/components/FeedbackModal'
import swalFeedback from '@src/mixins/swalFeedback.vue'
import validationMixin from '@src/mixins/validation.vue'
import fieldsValidation from '@src/mixins/fieldsValidation.vue'
import CancelButton from '@/src/components/CancelButton'
import SaveButton from '@/src/components/SaveButton'

const i18nCommon = 'COMMON'
const i18nKey = 'JUSTIFICATION'

export default {
    components: {
        FormModal,
        FeedbackModal,
        CancelButton,
        SaveButton,
    },
    mixins: [validationMixin, swalFeedback, fieldsValidation],
    data() {
        return {
            i18nKey: i18nKey,
            i18nCommon: i18nCommon,
            enableLimit: false,
            limitJustificationModal: {
                max_consecutive_uses: 0,
            },
            isEdit: false,
            submitLoading: false,
        }
    },
    validations: {
        limitJustificationModal: {
            max_consecutive_uses: {
                required,
                integer,
                minValue: minValue(1),
            },
        },
    },
    computed: {
        i18nTitleModal() {
            return this.getI18n(i18nKey, 'BUTTONS.limit_justification')
        },
        i18nLimitJustification() {
            return this.getI18nModified({
                prefix: i18nKey + '.BUTTONS.limit_justification',
                modifier: 1,
            })
        },
        i18nMaxConsecutiveUses() {
            return this.getI18n(i18nCommon, 'max_consecutive_uses')
        },
        i18nInvalidMaxConsecutiveUsesLabel() {
            return this.getI18n('ERROR_CODES.invalid_max_consecutive_uses')
        },
        modalOperation() {
            return 'edited'
        },
    },
    methods: {
        getInitialEnableLimitState() {
            let extraAttributes = this.$store.state.auth.currentUser.tenant.extra_attributes;

            if (extraAttributes && typeof extraAttributes === 'string') {
                extraAttributes = JSON.parse(extraAttributes);
            }

            if (!Object.hasOwn(extraAttributes, 'max_consecutive_uses')) {
                return false;
            }

            return extraAttributes.max_consecutive_uses > 0;
        },
        getInitialMaxConsecutiveUsesState() {
            let extraAttributes = this.$store.state.auth.currentUser.tenant.extra_attributes;

            if (extraAttributes && typeof extraAttributes === 'string') {
                extraAttributes = JSON.parse(extraAttributes);
            }

            if (!Object.hasOwn(extraAttributes, 'max_consecutive_uses')) {
                return 0;
            }

            return extraAttributes.max_consecutive_uses ?? 0;
        },
        showModal() {
            this.isEdit = true
            this.submitLoading = false

            this.enableLimit = this.getInitialEnableLimitState();
            this.limitJustificationModal.max_consecutive_uses = this.getInitialMaxConsecutiveUsesState();

            this.$refs.formModal.show()
            this.resetValidation()
        },
        clearAllResponseError() {
            this.modalResponseErrors = {}
        },
        cleanModal() {
            this.limitJustificationModal = Object.assign(
                this.limitJustificationModal,
                {
                    max_consecutive_uses: 0,
                }
            )
            this.$v.limitJustificationModal.$reset()
        },
        handleCancel() {
            this.resetValidation()
            this.$refs.formModal.hide()
        },
        resetValidation() {
            this.clearAllResponseError()
        },
        async handleSubmit() {
            this.submitLoading = true

            const operation = TenantService.updateAdminJustificationLimit(this.$store.getters['auth/tenantId'], {
                max_consecutive_uses: this.enableLimit
                    ? this.limitJustificationModal.max_consecutive_uses
                    : 0,
            })

            const response = await operation
                .then((response) => response)
                .catch((error) => error)


            this.$store.dispatch('auth/updateTenant', {
                tenant: response.data.data
            })

            const statusCode = response.status.toString()

            if (statusCode.charAt(0) === '2') {
                this.$emit('refresh')
                this.resetValidation()
                this.$refs.formModal.hide()
                this.positiveFeedback(
                    this.i18nLimitJustification,
                    this.modalOperation,
                    0
                )
            }

            if (statusCode === '422') {
                this.modalResponseErrors = response.data.errors
            }

            this.submitLoading = false
        },
    },
}
</script>
